import {
  Monitor,
  AlertCircle,
  AlertTriangle,
  Clock,
  ChevronRight,
  Wifi,
  HardDrive,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';

export interface StationCardData {
  displayName: string;
  errors: string;
  critical: string;
  lastreport: string;
  ports: string;
  id: string;
  maxDiskAge?: number;
}

interface StationCardProps {
  station: StationCardData;
  onClick?: () => void;
  showChevron?: boolean;
}

export function StationCard({ station, onClick, showChevron = true }: StationCardProps) {
  const lastReportDate = station.lastreport ? new Date(station.lastreport) : null;
  const daysSinceReport = lastReportDate
    ? Math.floor((Date.now() - lastReportDate.getTime()) / (1000 * 60 * 60 * 24))
    : null;
  const isStale = daysSinceReport !== null && daysSinceReport > 3;

  const errorLines = station.errors
    ? station.errors.split('\r\n').filter((e) => e.trim())
    : [];
  const errorCount = errorLines.length;

  const ports = station.ports
    ? station.ports.split(',').filter((p) => p.trim() && !p.includes('Error'))
    : [];
  const hasPortError = station.ports?.includes('Error');

  const hasCritical = !!station.critical?.trim();
  const diskAgeYears = station.maxDiskAge || 0;
  const diskAgeMonths = Math.round(diskAgeYears * 12);
  const isOldDisk = diskAgeYears >= 5;

  const Component = onClick ? 'button' : 'div';

  return (
    <Component
      className={`w-full p-3 rounded-lg border text-left transition-colors ${
        isStale || errorCount > 0 || hasCritical || isOldDisk
          ? 'bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-900 hover:bg-red-100 dark:hover:bg-red-900'
          : 'bg-card border-border hover:bg-muted/50'
      }`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Monitor className={`h-4 w-4 shrink-0 ${isStale || hasCritical ? 'text-red-500' : 'text-muted-foreground'}`} />
          <span className="font-medium text-sm truncate">{station.displayName}</span>
        </div>
        {showChevron && onClick && <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}
      </div>

      <div className="flex flex-wrap gap-1.5 mb-2">
        {isStale && daysSinceReport !== null && (
          <Badge variant="destructive" className="text-xs">
            <Clock className="h-3 w-3 mr-1" />
            {daysSinceReport} дн.
          </Badge>
        )}
        {errorCount > 0 && (
          <Badge variant="outline" className="text-xs bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300 border-red-300 dark:border-red-800">
            <AlertCircle className="h-3 w-3 mr-1" />
            {errorCount} ошибок
          </Badge>
        )}
        {hasCritical && (
          <Badge variant="outline" className="text-xs bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300 border-red-300 dark:border-red-800">
            <AlertTriangle className="h-3 w-3 mr-1" />
            {station.critical.includes('S.M.A.R.T') ? 'S.M.A.R.T!' : 'Критично'}
          </Badge>
        )}
        {ports.length > 0 && (
          <Badge variant="outline" className="text-xs">
            <Wifi className="h-3 w-3 mr-1" />
            {ports.length} портов
          </Badge>
        )}
        {diskAgeMonths > 0 && (
          <Badge variant="outline" className={`text-xs ${
            isOldDisk
              ? 'bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300 border-red-300 dark:border-red-800'
              : 'bg-muted text-muted-foreground'
          }`}>
            <HardDrive className="h-3 w-3 mr-1" />
            {diskAgeMonths} мес
          </Badge>
        )}
        {hasPortError && (
          <Badge variant="outline" className="text-xs bg-yellow-50 dark:bg-yellow-950 text-yellow-700 dark:text-yellow-300 border-yellow-300 dark:border-yellow-800">
            Ошибка портов
          </Badge>
        )}
      </div>

      {lastReportDate && (
        <div className="text-xs text-muted-foreground">
          Отчёт: {lastReportDate.toLocaleDateString('ru-RU')}
        </div>
      )}
    </Component>
  );
}
